import { defineSlotRecipe } from '@chakra-ui/react';

/*
 * v3 port notes:
 * - Slots renamed from v2's `tabsAnatomy`: `tab` → `trigger`, `tablist` →
 *   `list`, `tabpanel` → `content`. v3 drops the `tabpanels` plural wrapper
 *   slot (each `Tabs.Content` stands alone) and adds `contentGroup`/
 *   `indicator`, neither used by this app (no `Tabs.Indicator` anywhere).
 * - Chakra's built-in `tabs` recipe defaults to `variant: 'line'` (matching
 *   this file's own default) and deep-merges *under* this one, same
 *   mechanism as Checkbox.ts/Card.ts. Two consequences, confirmed
 *   empirically by resolving the merged recipe against a throwaway
 *   `createSystem` rather than just reading the built-in's source:
 *   1. `list`'s built-in *base* (applies regardless of variant) draws a
 *      permanent 1px `border.subtle`-colored bottom/end border — v2 never
 *      had this on `pill`/`centered`, so it's explicitly zeroed in `base`
 *      and re-added only where `line` wants it. `centered` never showed it:
 *      its v2 callers rendered `Tab`s straight inside `Tabs` with no
 *      `TabList`, so only the selected tab's own underline drew. v3 needs
 *      the `Tabs.List` wrapper, so `centered` leaves the list unstyled.
 *   2. The built-in's `line` variant draws the selected-tab underline via a
 *      `::before` pseudo-element positioned with `--indicator-*` CSS vars
 *      set *inside* the `[data-orientation=...]`-nested selected block —
 *      tried overriding just those two vars at the trigger's top level
 *      first, but the built-in's own vars are redeclared at a more nested
 *      selector on the same element and would win the cascade over a
 *      flatter override (unverifiable without a real browser render, so
 *      not worth trusting). `_before: { display: 'none' }` inside the same
 *      `[data-orientation=...]` nesting the built-in uses fully removes
 *      the pseudo-element regardless of cascade order, and `line`/
 *      `centered` draw the selected-state underline the same way v2 did —
 *      a real `borderBottomWidth`/`borderColor` on the trigger itself.
 * - `size` needs a real variant map here (v2 had one) — without it, the
 *   built-in's own `size: 'md'` default (very different `--tabs-height`/
 *   padding) bleeds through unopposed.
 */

// Hides the built-in `line`/`centered` variant's `::before` sliding
// indicator regardless of its `--indicator-*` CSS var values — see header
// note. This variant draws the underline itself via a real
// `borderBottomWidth` below instead.
const hideBuiltInIndicator = {
  '&[data-orientation=horizontal]': { _before: { display: 'none' } },
  '&[data-orientation=vertical]': { _before: { display: 'none' } },
};

const selectedLine = {
  ...hideBuiltInIndicator,
  color: 'fg.strong',
  borderColor: 'tIndigo.base',
  fontWeight: 600,
  // Triggers rendered as a real anchor (`MCustomLinkTab`) would otherwise pick
  // up designSystemV2.css's global `a:hover` underline.
  textDecoration: 'none',
};

const lineTrigger = {
  fontWeight: 500,
  fontSize: 'sm', // 13px
  paddingStart: '2px',
  paddingEnd: '2px',
  marginRight: '1.5rem',
  // The built-in trigger has a fixed `height` and `minW: var(--tabs-height)`,
  // so a cramped tab list shrinks triggers below their longest word and the
  // wrapped label spills out of the fixed-height box onto its neighbors.
  // Restore v2's behavior: shrink no further than the longest word
  // (`minW: auto`) and grow to fit a wrapped label.
  minW: 'auto',
  height: 'auto',
  minH: 'var(--tabs-height)',
  borderBottomWidth: '2px',
  borderColor: 'transparent',
  marginBottom: '-3px',
  color: 'fg.muted',
  textDecoration: 'none',
  _selected: selectedLine,
  _hover: {
    color: 'fg.primary',
    textDecoration: 'none',
    _selected: selectedLine,
  },
};

const lineList = {
  borderBottomWidth: '1px',
  borderColor: 'border.subtle',
  paddingBottom: 0,
  // 4px top padding leaves room for the tab focus halo (3px outset shadow)
  // when the TabList sits flush against a drawer/modal/page top edge.
  // Without it, the top of the halo gets clipped.
  paddingTop: 1,
};

export const tabsTheme = defineSlotRecipe({
  slots: ['root', 'trigger', 'list', 'content', 'contentGroup', 'indicator'],
  base: {
    root: {
      marginBottom: 6,
    },
    // Built-in base always draws a 1px list border regardless of variant —
    // v2 never had this on `pill`/`centered`, so it's zeroed here and
    // reintroduced by `line` only.
    list: {
      borderBottomWidth: 0,
      borderInlineEndWidth: 0,
      justifyContent: 'flex-start',
    },
    trigger: {
      transitionProperty: 'common',
      transitionDuration: 'normal',
      _focusVisible: {
        zIndex: 1,
        boxShadow: 'focus',
        outline: 'none',
      },
      _disabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
      },
      _active: {
        bg: 'none',
      },
    },
    // Chakra adds `tabIndex={0}` to the content panel for screen-reader
    // access; a visible halo around the entire panel reads as wrong, so it's
    // suppressed here (same selector depth as the built-in's own
    // `focusVisibleRing: 'inside'` block, so this reliably overrides it).
    content: {
      padding: 4,
      _focusVisible: {
        boxShadow: 'none',
        outline: 'none',
      },
    },
  },
  variants: {
    fitted: {
      true: {
        list: { display: 'flex' },
        trigger: { flex: 1, justifyContent: 'center' },
      },
    },
    align: {
      start: { list: { justifyContent: 'flex-start' } },
      center: { list: { justifyContent: 'center' } },
      end: { list: { justifyContent: 'flex-end' } },
    },
    variant: {
      line: {
        list: lineList,
        trigger: lineTrigger,
      },
      centered: {
        root: {
          backgroundColor: 'bg.surface',
          padding: '.25rem 0 .5rem 0',
          display: 'flex',
          justifyContent: 'center',
        },
        trigger: lineTrigger,
      },
      pill: {
        list: {
          display: 'inline-flex',
          gap: 1,
          bg: 'bg.surface',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'border.subtle',
          borderRadius: 'full',
          p: 1,
          boxShadow: 'xs',
        },
        trigger: {
          fontWeight: 500,
          fontSize: 'sm',
          color: 'fg.muted',
          borderRadius: 'full',
          px: 4,
          py: 2,
          gap: 2,
          textDecoration: 'none',
          _selected: {
            color: 'fg.onAccent',
            bg: 'tIndigo.base',
            fontWeight: 600,
          },
          _hover: {
            color: 'fg.primary',
            bg: 'bg.hover',
            textDecoration: 'none',
            _selected: {
              color: 'fg.onAccent',
              bg: 'tIndigo.base',
            },
          },
        },
      },
    },
    size: {
      sm: {
        trigger: { py: 1, fontSize: 'sm' },
      },
      md: {
        trigger: { fontSize: 'sm', py: 2, px: 4 },
      },
      lg: {
        trigger: { fontSize: 'lg', py: 3, px: 4 },
      },
    },
  },
  defaultVariants: {
    variant: 'line',
    size: 'md',
  },
});

export default tabsTheme;
